import React, { useState } from 'react'
import { ReactComponent as MoreIcon } from '../../icons/more.svg'
import { ReactComponent as LessIcon } from '../../icons/less.svg'

import './RatingSlider.scss'

type Ratings = Record<string, number>

interface Criterion {
  key: string
  label: string
  min: number
  max: number
  explanation: string // HTML content
}

interface RatingsSliderProps {
  criteria: Criterion[]
  ratings: Ratings
  step: number
  onChange: (key: string, value: number) => void
}

export default function RatingsSlider ({
  criteria,
  ratings,
  step,
  onChange
}: RatingsSliderProps): React.ReactElement {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const getSliderBackground = (value: number, min: number, max: number): string => {
    const percentage = ((value - min) / (max - min)) * 100
    return `linear-gradient(to right, #f37845 ${percentage}%, #dadbe2 ${percentage}%)`
  }

  return (
    <div className='ratings-slider'>
      {criteria.map((criterion) => {
        const isExpanded = expanded[criterion.key] ?? false

        return (
          <div key={criterion.key} className='slider-container'>
            {/* Label and Slider Row */}
            <div className="slider-row">
              <div className="slider-label">
                <button
                  onClick={(e) => {toggleExpanded(criterion.key) ; e.preventDefault(); e.stopPropagation() }}
                  className="expand-toggle"
                  aria-label={isExpanded ? 'Collapse explanation' : 'Expand explanation'}
                >
                  {isExpanded ? <LessIcon /> : <MoreIcon />}
                </button>
                <span>{criterion.label}</span>
              </div>

              <div className="slider-wrapper">
                <input
                  type="range"
                  min={criterion.min}
                  max={criterion.max}
                  step={step}
                  value={ratings[criterion.key] ?? criterion.min}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value)
                    onChange(criterion.key, value)
                  }}
                  style={{
                    background: getSliderBackground(
                      ratings[criterion.key] ?? criterion.min,
                      criterion.min,
                      criterion.max
                    )
                  }}
                />
                <span>{ratings[criterion.key]}</span>
              </div>
            </div>

            {/* Explanation Section */}
            {isExpanded && (
              <div
                className="slider-explanation"
                dangerouslySetInnerHTML={{ __html: criterion.explanation }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
